/**
 * useCameraViewport — iOS-safe full-screen camera shell hook.
 *
 * The bug we're solving (Iter. 406.d):
 *   • `fixed inset-0` + Tailwind `h-screen` resolve to `100vh` on iOS Safari,
 *     which INCLUDES the URL bar — even when it's retracted. Result on iPhone:
 *     the close (X) button gets pushed above the visible area and the shutter
 *     button gets pushed below it, so the user has to scroll inside what was
 *     supposed to be a fixed modal.
 *   • The notch / Dynamic Island (iPhone X+) and the home indicator
 *     reserve 47-59px top + 34px bottom — invisible to `inset-0`.
 *
 * The fix:
 *   • Use `100dvh` (dynamic viewport height) when supported.
 *   • Fall back to a JS-measured `window.innerHeight` (px) for iOS < 15.4
 *     and Android browsers that don't ship dvh yet.
 *   • Add `env(safe-area-inset-top/bottom)` as padding so the chrome
 *     doesn't sit under the notch or the home indicator.
 *   • Lock body scroll while the camera is open (prevents pull-to-refresh
 *     and the rubber-band effect from displacing the modal).
 *
 * Usage:
 *   const { containerStyle } = useCameraViewport({ open });
 *   return <div style={containerStyle} className="fixed inset-0 ...">...</div>;
 *
 * The container should also use `flex flex-col` so the three zones (top bar,
 * video, bottom bar) split height correctly. Make the middle zone `flex-1
 * min-h-0` so it shrinks instead of pushing the bottom bar off-screen.
 */
import { useEffect, useMemo, useState } from 'react';

// Probe dvh support once at module load — cheap and stable across renders.
const SUPPORTS_DVH = (() => {
  if (typeof window === 'undefined' || typeof CSS === 'undefined' || !CSS.supports) return false;
  try {
    return CSS.supports('height', '100dvh');
  } catch {
    return false;
  }
})();

export function useCameraViewport({ open = true, lockBodyScroll = true, allowInternalScroll = false } = {}) {
  // Live window.innerHeight — only used as fallback when dvh is unsupported.
  const [innerH, setInnerH] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 0
  );

  useEffect(() => {
    if (!open || SUPPORTS_DVH) return undefined;
    const onResize = () => setInnerH(window.innerHeight);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    // iOS visualViewport handles soft-keyboard + URL bar transitions better
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onResize);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onResize);
      }
    };
  }, [open]);

  // Lock body scroll while open — prevents iOS rubber-band & pull-to-refresh
  // from shifting the modal contents off-screen.
  useEffect(() => {
    if (!open || !lockBodyScroll || typeof document === 'undefined') return undefined;
    const body = document.body;
    const html = document.documentElement;
    const prevOverflow = body.style.overflow;
    const prevPosition = body.style.position;
    const prevHtmlOverflow = html.style.overflow;
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.width = '100%';
    html.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prevOverflow;
      body.style.position = prevPosition;
      body.style.width = '';
      html.style.overflow = prevHtmlOverflow;
    };
  }, [open, lockBodyScroll]);

  const containerStyle = useMemo(
    () => ({
      // Height — prefer dvh, fallback to measured px so iOS < 15.4 still works.
      height: SUPPORTS_DVH ? '100dvh' : `${innerH}px`,
      maxHeight: SUPPORTS_DVH ? '100dvh' : `${innerH}px`,
      // Safe-area insets — iPhone X+ notch / Dynamic Island + home indicator.
      paddingTop: 'env(safe-area-inset-top, 0px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      paddingLeft: 'env(safe-area-inset-left, 0px)',
      paddingRight: 'env(safe-area-inset-right, 0px)',
      // Hard-lock any internal scroll attempt — the modal lays itself out
      // entirely within the viewport. Set to 'auto' when the modal content
      // legitimately needs to scroll (long consent forms, onboarding wizard).
      overflow: allowInternalScroll ? 'auto' : 'hidden',
      // Prevent iOS Safari from triggering page zoom / native gestures
      // on the camera surface.
      touchAction: allowInternalScroll ? 'pan-y' : 'none',
      overscrollBehavior: 'contain',
      WebkitOverflowScrolling: allowInternalScroll ? 'touch' : 'auto',
    }),
    [innerH, allowInternalScroll]
  );

  return { containerStyle, supportsDvh: SUPPORTS_DVH };
}

export default useCameraViewport;
